/**
 * The Fleet feed panel's data source (M6 plan P35), Node-tested: reads the captain log (`fleetFeed`) and the
 * corporation's claims (`corpView`, diffed by `trackClaims`) on `refresh()` and builds the rows. UI events only.
 */
import { logger } from '../../core/logger'
import type { CorpView, FleetLogEntry } from '../../strategic/types'
import { createClaimTracker, feedRows, trackClaims, type ClaimTracker, type FeedRow, type FleetFeedConfig } from './model'

export interface FeedSource {
  fleetFeed(limit: number): Promise<FleetLogEntry[]>
  corpView(): Promise<CorpView>
  /** The strategic clock now (stamps a claim change), null before the first advance. */
  clock(): { time: number; day: number } | null
}

export interface FeedController {
  readonly rows: readonly FeedRow[]
  readonly claims: ClaimTracker
  /** Re-reads the feed and the claims; resolves when the rows are in. */
  refresh(): Promise<void>
  onChange(fn: () => void): () => void
}

export function createFeedController(src: FeedSource, cfg: FleetFeedConfig, systemName: (id: string) => string): FeedController {
  const claims = createClaimTracker()
  const fns = new Set<() => void>()
  let rows: readonly FeedRow[] = []
  let logs: readonly FleetLogEntry[] = []

  const c: FeedController = {
    get rows(): readonly FeedRow[] {
      return rows
    },
    claims,
    async refresh(): Promise<void> {
      try {
        const [feed, corp] = await Promise.all([src.fleetFeed(cfg.feedLimit), src.corpView()])
        logs = feed
        const now = src.clock() ?? { time: feed[0]?.t ?? 0, day: feed[0]?.day ?? 0 }
        trackClaims(claims, corp.founded ? corp.claims : [], { day: now.day, t: now.time }, systemName, cfg.labels, cfg.claimKeep)
      } catch (err) {
        logger.warn('fleet feed: refresh failed', { err: String(err) })
      }
      rows = feedRows(logs, claims.rows, cfg.feedLimit)
      for (const fn of fns) fn()
    },
    onChange(fn: () => void): () => void {
      fns.add(fn)
      return () => fns.delete(fn)
    },
  }
  return c
}
